import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, AppState, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { setDoc, useDoc } from '../../store/store';
import { Section, Sheet } from '../../components/ui';
import { C, alpha } from '../../components/theme';
import { brDateTime } from '../../lib/date';
import { t } from '../../lib/i18n';
import {
  NOME, atualizarNuvem, conectar, copiaAutomatica, desconectar, enviarPendentes, fazerCopia, listarCopias,
  pendentesNoAparelho, restaurarCopia, useNuvem, type Copia, type Provedor,
} from '../../lib/nuvem';
import { Card } from './comum';
import { BotaoAcao, Divisor, LinhaChave, LinhaEscolha, LinhaOpcoes } from '../../components/ajustes/Linhas';

const ICONE: Record<Provedor, string> = { google: '🟢', microsoft: '🔵' };
const erro = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));

/** Ajustes > Google Drive e OneDrive: conectar, onde guardar os anexos, cópias de segurança. */
export default function Nuvem({ cor }: { cor: string }) {
  const n = useNuvem();
  const aj = useDoc('ajustes');
  useDoc('estudos'); useDoc('documentos');   // atualiza a contagem de arquivos do aparelho
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [copias, setCopias] = useState<{ p: Provedor; lista: Copia[] } | null>(null);

  // Atualiza ao abrir e ao voltar do navegador (depois de autorizar no Google/Microsoft).
  useEffect(() => {
    atualizarNuvem();
    const sub = AppState.addEventListener('change', s => { if (s === 'active') atualizarNuvem(); });
    const foco = () => atualizarNuvem();
    if (Platform.OS === 'web') window.addEventListener('focus', foco);
    return () => { sub.remove(); if (Platform.OS === 'web') window.removeEventListener('focus', foco); };
  }, []);

  const rodar = async (_: string, fn: () => Promise<unknown>) => {
    setOcupado(t('Aguarde...'));
    try { await fn(); } catch (e) { erro(e); } finally { setOcupado(null); }
  };
  const destino = aj.nuvem?.destino ?? null;
  const pend = pendentesNoAparelho();
  const conectadas = n.contas.map(c => c.provedor);
  // 0.73: Drive/OneDrive sem configuração no servidor ficam escondidos (nada de "Em breve", regra 2.1 da Apple)
  const visiveis = (['google', 'microsoft'] as Provedor[]).filter(p => conectadas.includes(p) || n.configurado[p]);

  if (!n.carregado) return <ActivityIndicator color={cor} style={{ marginTop: 30 }} />;

  return (<>
    {visiveis.length > 0 && <Text style={{ color: C.sub }}>{t('Ligue o seu Google Drive ou OneDrive para guardar fotos e arquivos (que passam a abrir em qualquer dispositivo) e para ter cópias de segurança completas.')}</Text>}
    {!!n.erro && <Card><Text style={{ color: C.warn }}>{n.erro}</Text><BotaoAcao cor={cor} label={'🔄 ' + t('Tentar de novo')} onPress={atualizarNuvem} /></Card>}

    {visiveis.map(p => {
      const c = n.contas.find(x => x.provedor === p);
      return (
        <View key={p} style={{ gap: 0 }}>
          <Section t={NOME[p]} />
          <Card>
            {c ? (<>
              <Text style={{ color: C.ok, fontWeight: '700' }}>✓ {t('Conectado')}{c.email ? ` · ${c.email}` : ''}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>
                {c.ultimo_backup ? t('Última cópia de segurança: {data}', { data: brDateTime(c.ultimo_backup) }) : t('Nenhuma cópia de segurança ainda.')}
              </Text>
              <LinhaChave icone="🗓️" titulo={t('Cópia automática toda semana')} cor={cor} valor={c.auto_backup} onMudar={v => rodar('auto', () => copiaAutomatica(p, v))} />
              <Divisor />
              <LinhaEscolha icone="💾" titulo={t('Fazer cópia agora')} onPress={() => rodar('copia' + p, async () => {
                const r = await fazerCopia(p); Alert.alert(t('Cópia feita'), t('"{nome}" está no {provedor}.', { nome: r.nome, provedor: NOME[p] }));
              })} />
              <Divisor />
              <LinhaEscolha icone="↩️" titulo={t('Restaurar uma cópia')} onPress={() => rodar('lista' + p, async () => setCopias({ p, lista: await listarCopias(p) }))} />
              <View>
                <BotaoAcao cor={C.danger} label={t('Desconectar')} onPress={() => Alert.alert(t('Desconectar o {nome}?', { nome: NOME[p] }),
                  t('Os arquivos e cópias que já estão lá continuam lá. O My Planner só deixa de guardar coisas novas.'), [
                    { text: t('Cancelar'), style: 'cancel' }, { text: t('Desconectar'), style: 'destructive', onPress: () => rodar('sai' + p, () => desconectar(p)) }])} />
              </View>
            </>) : (<>
              <Text style={{ color: C.sub, fontSize: 12 }}>
                {p === 'google'
                  ? t('O My Planner cria a pasta "My Planner" no seu Drive e só enxerga o que ele mesmo guardou lá.')
                  : t('O My Planner usa só a pasta Aplicativos › My Planner do seu OneDrive.')}
              </Text>
              <BotaoAcao cheio cor={cor} label={`${ICONE[p]} ${t('Conectar {nome}', { nome: NOME[p] })}`} onPress={() => rodar('con' + p, () => conectar(p))} />
            </>)}
          </Card>
        </View>
      );
    })}

    <Section t={t('Onde guardar fotos e arquivos novos')} />
    <Card>
      <LinhaOpcoes icone="📁" titulo={t('Guardar em')} valor={destino ?? 'aparelho'}
        opcoes={[['aparelho', '📱 ' + t('Só neste dispositivo')] as [string, string], ...conectadas.map(p => [p, `${ICONE[p]} ${NOME[p]}`] as [string, string])]}
        onEscolher={v => setDoc('ajustes', d => ({ ...d, nuvem: { destino: v === 'aparelho' ? null : v as Provedor } }))} />
      <Text style={{ color: C.sub, fontSize: 12 }}>{Platform.OS === 'android'
        ? t('O app guarda só o atalho do arquivo escolhido: ele continua onde está (Drive, OneDrive, Downloads) e abre no app do celular. Nada é copiado. Esta opção vale para as fotos tiradas pela câmera.')
        : t('Vale para Estudos e Documentos. Na nuvem, o arquivo abre em qualquer dispositivo e no My Planner Web (até 15 MB por arquivo).')}</Text>
      {pend > 0 && !!destino && (
        <LinhaEscolha icone="⬆️" cor={cor} titulo={t('Enviar para o {nome} os {n} arquivo(s) deste dispositivo', { nome: NOME[destino], n: pend })}
          onPress={() => rodar('pend', async () => {
            const r = await enviarPendentes(destino, (f, tot) => setOcupado(t('Enviando {f} de {n}...', { f, n: tot })));
            Alert.alert(t('Pronto'), r.falhas ? t('{ok} enviado(s), {falhas} com erro. Tente de novo mais tarde.', { ok: r.ok, falhas: r.falhas }) : t('{ok} enviado(s).', { ok: r.ok }));
          })} />
      )}
    </Card>

    <Section t={t('Escolher arquivos do Drive ou do OneDrive')} />
    <Card>
      <Text style={{ color: C.sub, fontSize: 12 }}>
        {Platform.OS === 'web'
          ? t('No navegador, cole o link do arquivo do Drive, OneDrive ou Dropbox (em Estudos ou Documentos, opção Link). No celular, o botão de arquivo já mostra o Drive e o OneDrive.')
          : t('Ao tocar em "Arquivo" ou "Foto" em Estudos e Documentos, o seletor do celular mostra o Google Drive, o OneDrive e o Dropbox (quando os apps estão instalados). Escolha o arquivo direto de lá.') + ' '
            + t('Ao tocar num arquivo anexado, ele abre no app do celular que você escolher (se o celular perguntar, "Apenas uma vez" ou "Sempre"). O que você editar lá fica salvo no próprio arquivo.')}
      </Text>
    </Card>

    {!!ocupado && (
      <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center', padding: 12, borderRadius: 12, backgroundColor: alpha(cor, 0.15) }}>
        <ActivityIndicator color={cor} />
        <Text style={{ color: C.text }}>{ocupado}</Text>
      </View>
    )}

    <Sheet visible={!!copias} onClose={() => setCopias(null)} title={t('Restaurar uma cópia')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 8, paddingBottom: 60 }}>
        <Text style={{ color: C.warn }}>{t('Restaurar troca TODOS os dados atuais pelos da cópia, aqui e na sua conta.')}</Text>
        {copias && !copias.lista.length && <Text style={{ color: C.sub }}>{t('Nenhuma cópia encontrada.')}</Text>}
        {copias?.lista.map(c => (
          <Pressable key={c.id} onPress={() => Alert.alert(t('Restaurar esta cópia?'), brDateTime(c.criado), [
            { text: t('Cancelar'), style: 'cancel' },
            { text: t('Restaurar'), style: 'destructive', onPress: () => { const p = copias.p; setCopias(null); rodar('rest', async () => { await restaurarCopia(p, c.id); Alert.alert(t('Cópia restaurada.')); }); } }])}
            style={({ pressed }) => ({ padding: 14, borderRadius: 12, backgroundColor: pressed ? C.card2 : C.card, gap: 2 })}>
            <Text style={{ color: C.text, fontWeight: '700' }}>💾 {brDateTime(c.criado)}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{c.nome} · {Math.max(1, Math.round(c.tamanho / 1024))} KB</Text>
          </Pressable>
        ))}
      </ScrollView>
    </Sheet>
  </>);
}
